import { useCallback, useMemo, useState } from 'react';
import {
  ComplianceCoverageBranch,
  SupplierComplianceCoverageStatus,
} from '@texas/api/endpoints/variantsApi';
import { useArticleContext } from '@texas/components/article/articleContext/useArticleContext';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';

interface Filters {
  search: string;
  branchId: number | null;
  supplierId: number | null;
  status: SupplierComplianceCoverageStatus | null;
}

interface Props {
  coverageBranches: ComplianceCoverageBranch[] | undefined;
  variantId: number;
}

const defaultFilters: Filters = {
  search: '',
  branchId: null,
  supplierId: null,
  status: null,
};

export function useFilters({ coverageBranches, variantId }: Props) {
  const context = useArticleContext();
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const { search, branchId, supplierId, status } = filters;
  const updateFilters = useCallback((values: Partial<Filters>) => {
    setFilters((current) => ({ ...current, ...values }));
  }, []);
  const scopedBranches = useMemo(
    () =>
      (coverageBranches ?? []).filter(
        (branch) =>
          context.variantId !== variantId ||
          context.branchIds.length === 0 ||
          context.branchIds.includes(branch.id),
      ),
    [coverageBranches, context, variantId],
  );
  const effectiveBranchId = scopedBranches.some(
    (branch) => branch.id === branchId,
  )
    ? branchId
    : null;
  const availableBranches = scopedBranches.filter(
    (branch) => effectiveBranchId === null || branch.id === effectiveBranchId,
  );
  const suppliers = [
    ...new Map(
      availableBranches
        .flatMap((branch) => branch.suppliers)
        .map((supplier) => [supplier.id, supplier]),
    ).values(),
  ].sort((a, b) => a.name.localeCompare(b.name));
  const effectiveSupplierId = suppliers.some(
    (supplier) => supplier.id === supplierId,
  )
    ? supplierId
    : null;
  const branches = availableBranches
    .map((branch) => {
      const visibleSuppliers = branch.suppliers.filter(
        (supplier) =>
          effectiveSupplierId === null || supplier.id === effectiveSupplierId,
      );
      return {
        ...branch,
        suppliers: visibleSuppliers,
        requirements: branch.requirements.filter(
          (requirement) =>
            requirement.name
              .toLocaleLowerCase()
              .includes(search.trim().toLocaleLowerCase()) &&
            (status === null ||
              visibleSuppliers.some((supplier) =>
                supplier.coverage.some(
                  (item) =>
                    item.definitionId === requirement.definitionId &&
                    compareEnum(
                      SupplierComplianceCoverageStatus,
                      item.status,
                      status,
                    ),
                ),
              )),
        ),
      };
    })
    .filter(
      (branch) =>
        (effectiveSupplierId === null || branch.suppliers.length > 0) &&
        ((!search.trim() && status === null) || branch.requirements.length > 0),
    );

  return {
    filters,
    updateFilters,
    scopedBranches,
    effectiveBranchId,
    effectiveSupplierId,
    suppliers,
    branches,
  };
}
